import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { Center, Loader } from '@mantine/core';
import { AuthProvider, useRequireAdmin } from './lib/auth';
import type { SectionConfig } from './lib/types';
import SignIn from './pages/SignIn';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import SectionList from './pages/SectionList';
import SectionEdit from './pages/SectionEdit';
import { productSection, categorySection, productStatusSection, downloadSection } from './sections/products';
import { deliverySection, deliveryCountrySection, orderStatusSection } from './sections/commerce';
import { referenceSection, partnerSection, teamSection, featureCardSection } from './sections/content';
import { customerSection, customerStatusSection, countrySection, downloadLogSection } from './sections/customers';
import { adminUserSection } from './sections/admin';
import OrderList from './pages/OrderList';
import OrderDetail from './pages/OrderDetail';
import PageManager from './pages/PageManager';

function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useRequireAdmin();
  if (isLoading) return <Center h="100vh"><Loader /></Center>;
  if (!isAuthenticated) return <Navigate to="/sign-in" replace />;
  return <>{children}</>;
}

function sectionRoutes(config: SectionConfig, section: string, subsection: string) {
  return (
    <Route key={config.id} path={`${section}/${subsection}`}>
      <Route index element={<SectionList config={config} />} />
      <Route path=":id" element={<SectionEdit config={config} />} />
    </Route>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/sign-in" element={<SignIn />} />
          <Route
            element={
              <AuthGuard>
                <Layout />
              </AuthGuard>
            }
          >
            <Route index element={<Dashboard />} />

            {/* Products */}
            {sectionRoutes(productSection, 'products', 'pro')}
            {sectionRoutes(categorySection, 'products', 'cat')}
            {sectionRoutes(productStatusSection, 'products', 'pst')}
            {sectionRoutes(downloadSection, 'products', 'dwn')}

            {/* Orders */}
            <Route path="orders/ord" element={<OrderList />} />
            <Route path="orders/ord/:id" element={<OrderDetail />} />

            {/* Setup */}
            {sectionRoutes(deliverySection, 'setup', 'del')}
            {sectionRoutes(deliveryCountrySection, 'setup', 'cpr')}
            {sectionRoutes(orderStatusSection, 'setup', 'ost')}

            {/* Content */}
            {sectionRoutes(referenceSection, 'content', 'ref')}
            {sectionRoutes(partnerSection, 'content', 'par')}
            {sectionRoutes(teamSection, 'content', 'tea')}
            {sectionRoutes(featureCardSection, 'content', 'fea')}

            {/* Customers */}
            {sectionRoutes(customerSection, 'customers', 'cus')}
            {sectionRoutes(customerStatusSection, 'customers', 'cst')}
            {sectionRoutes(countrySection, 'customers', 'cou')}
            {sectionRoutes(downloadLogSection, 'customers', 'dlo')}

            {/* CMS */}
            <Route path="cms/pag" element={<PageManager />} />

            {/* Administration */}
            {sectionRoutes(adminUserSection, 'admin', 'adm')}
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
